'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { LinkButton } from '@/components/ui/button'
import { FAQS } from './faqs'
import './vector-page.css'

/**
 * /vector — a direct port of `public/radar-branding/vector-new-page.html`.
 *
 * That file is the design contract: markup, class names, copy, and section
 * order match it one-for-one so the two can be diffed. Styling lives in the
 * sibling `vector-page.css`, scoped under `.vector-page`.
 *
 * Two blocks of the live page are deliberately kept and are NOT ported from
 * the mock (Lloyd's call, 2026-08-12):
 *
 *   - `IntegrationDiagram` replaces the mock's own `.orbit-section`. The mock
 *     redraws the same twelve dimensions, so porting it would have produced a
 *     second orbit; the existing component ships instead, unchanged.
 *   - `PilotCTA` stays as the closing lead capture, anchored at
 *     `#strategy-call` so the mock's own "Schedule strategy call" buttons
 *     resolve to it. The mock has no form at all.
 *
 * Both render as SIBLINGS of the ported fragments rather than inside them.
 * `.vector-page` redefines custom properties the site also uses and restyles
 * bare `p`, so nesting them would silently change how they look. That is why
 * this file exports three fragments instead of one tree — `page.tsx`
 * interleaves them with the retained components.
 */

const HERO_FACTS = [
  ['Best for', '50–200+ inbound leads / month'],
  ['Delivery', '60-day fixed-scope build'],
  ['Ownership', 'Your application and infrastructure'],
] as const

const MINI_SCORES = [
  ['Intent', '8 / 10', '80%', '#31d17c'],
  ['Budget', '7 / 10', '70%', '#008dcc'],
  ['Timeline', '9 / 10', '90%', '#755be8'],
  ['Authority', '8 / 10', '80%', '#fdc304'],
  ['Need', '9 / 10', '90%', '#f90b8a'],
  ['Fit', '7 / 10', '70%', '#16bce9'],
] as const

const QUALIFIER_CELLS = [
  ['Volume', '50–200+', 'Inbound leads per month'],
  ['Scoring', '12D', 'Real qualification dimensions'],
  ['CRM', 'Standard', 'HubSpot · Salesforce · Pipedrive'],
  ['Build', '60 days', 'Fixed scope · client-owned'],
] as const

const FIT_CARDS = [
  [
    '01',
    'Steady inbound',
    'Enough lead volume to reveal patterns, measure lift, and improve the scoring model.',
    // Fill token, not the ink one: --v-cyan-bright darkens in the light theme.
    'var(--v-cyan-fill)',
  ],
  [
    '02',
    'Defined ICP',
    'Your team knows the accounts, problems, and signals that make a conversation worth pursuing.',
    'var(--v-pink)',
  ],
  [
    '03',
    'Receiving team',
    'An SDR or seller is ready to act when Vector identifies a high-signal opportunity.',
    'var(--v-yellow)',
  ],
  [
    '04',
    'Standard CRM',
    'Your qualification rationale and next move can be written into the tools your team already uses.',
    'var(--v-green)',
  ],
] as const

/** The twelve rows of the scoring panel, with their fixed track colours. */
const SCORE_ROWS = [
  ['Intent', '#10b85a'],
  ['Budget', '#008dcc'],
  ['Timeline', '#6754d8'],
  ['Authority', '#d8890b'],
  ['Need', '#d72d58'],
  ['Fit', '#16bce9'],
  ['Engage', '#8357dd'],
  ['Emotion', '#ed6724'],
  ['Context', '#1fb9a5'],
  ['Knowledge', '#d640df'],
  ['Integration', '#6754d8'],
  ['Orchestrate', '#10b85a'],
] as const

const PROFILES = {
  qualified: {
    tab: 'Qualified lead',
    badge: 'Qualified · high signal',
    name: 'Sarah Chen',
    role: 'VP of Operations · TechScale Inc.',
    score: '94',
    route: 'Route to Senior AE ↗',
    outcome: 'Meeting booked · closed in 23 days',
    rationale:
      'Clear buying signals · active project this quarter · VP-level authority · explicit operational pain',
    values: [8, 7, 9, 8, 9, 7, 8, 6, 8, 7, 9, 8],
    color: '#167843',
    background: '#e8f8ef',
  },
  filtered: {
    tab: 'Filtered lead',
    badge: 'Filtered · nurture',
    name: 'John Smith',
    role: 'Marketing Intern · Unknown Startup',
    score: '31',
    route: 'Auto-nurture sequence →',
    outcome: '45 minutes of SDR review time avoided',
    rationale:
      'Low purchase intent · no active budget · authority not established · exploratory timeline',
    values: [2, 1, 3, 1, 4, 2, 5, 3, 4, 2, 2, 2],
    color: '#9a4d00',
    background: '#fff0dc',
  },
} as const

type ProfileKey = keyof typeof PROFILES

type HeroPhase = 'received' | 'scoring' | 'routed'

const HERO_STATUS: Record<HeroPhase, string> = {
  received: 'Signal received',
  scoring: 'Scoring live',
  routed: 'Route ready',
}

const SAFETY_CARDS = [
  [
    '01 / Visible',
    'Full rationale',
    'Every route keeps the twelve scores and the evidence behind the decision.',
  ],
  [
    '02 / Daily',
    'Spot-check report',
    'Review filtered leads every morning before an edge case becomes a missed opportunity.',
  ],
  [
    '03 / Human',
    'One-click override',
    'Escalate any lead immediately. Nothing is permanently disqualified without review.',
  ],
  [
    '04 / Weekly',
    'Outcome comparison',
    'Compare Vector’s calls with real outcomes and adjust weights when the evidence changes.',
  ],
] as const

const OWNS = [
  'Application source code',
  'User interface and workflows',
  'CRM integrations and fields',
  'API routes and business logic',
  'Deployment infrastructure',
] as const

const MAINTAINS = [
  'Intelligence platform access',
  'Scoring engine and models',
  'Ongoing model training',
  'LLM infrastructure and costs',
  'Security updates and support',
] as const

const PROCESS = [
  [
    '01',
    'Baseline',
    'Measure volume, response time, qualification rate, conversion by source, and the rules your team uses today.',
    'Week 1',
  ],
  [
    '02',
    'Configure',
    'Translate your ICP, evidence, scoring weights, CRM fields, and routing thresholds into the Vector system.',
    'Weeks 2–4',
  ],
  [
    '03',
    'Integrate',
    'Connect the application to your site, CRM, handoff workflows, security controls, and operator review.',
    'Weeks 5–6',
  ],
  [
    '04',
    'Calibrate',
    'Run real conversations, compare the calls, tune the model, and launch with a weekly measurement rhythm.',
    'Weeks 7–8',
  ],
] as const

/**
 * Reveal-on-scroll, shared by all three fragments. The source hides
 * `[data-reveal]` unconditionally in CSS; here the hidden state only applies
 * once `is-enhanced` lands, so a blocked script leaves the page readable.
 */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)

  useEffect(() => setEnhanced(true), [])

  useEffect(() => {
    const root = ref.current
    if (!root) return
    const targets = root.querySelectorAll<HTMLElement>('[data-reveal]')
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('v-shown'))
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('v-shown')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -35px' }
    )
    targets.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return { ref, className: `vector-page${enhanced ? ' is-enhanced' : ''}` }
}

/** Hero, the fit rail, and the qualification-gap section. */
export function VectorPageTop() {
  const { ref, className } = useReveal()
  const heroConsoleRef = useRef<HTMLDivElement>(null)
  const heroTimers = useRef<number[]>([])
  const [heroPhase, setHeroPhase] = useState<HeroPhase>('routed')

  const playHeroSequence = useCallback(() => {
    heroTimers.current.forEach(window.clearTimeout)
    heroTimers.current = []

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setHeroPhase('routed')
      return
    }

    setHeroPhase('received')
    heroTimers.current.push(
      window.setTimeout(() => setHeroPhase('scoring'), 220),
      window.setTimeout(() => setHeroPhase('routed'), 1280)
    )
  }, [])

  useEffect(() => {
    const console = heroConsoleRef.current
    if (!console) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reduced.matches) return

    if (typeof IntersectionObserver === 'undefined') {
      heroTimers.current.push(window.setTimeout(playHeroSequence, 280))
      return () => heroTimers.current.forEach(window.clearTimeout)
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          heroTimers.current.push(window.setTimeout(playHeroSequence, 280))
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(console)

    return () => {
      observer.disconnect()
      heroTimers.current.forEach(window.clearTimeout)
    }
  }, [playHeroSequence])

  return (
    <div ref={ref} className={className}>
      <section className='hero' aria-labelledby='hero-title'>
        <div className='hero-grid'>
          <div className='hero-copy'>
            <p className='eyebrow'>Vector points · shows the move</p>
            <h1 id='hero-title'>
              Qualify every inbound lead. Route the{' '}
              <span className='pink'>right ones</span> in seconds.
            </h1>
            <p className='hero-lede'>
              <strong>
                Vector scores every conversation across 12 dimensions, explains
                the signal, and routes the next move.
              </strong>{' '}
              Built in 60 days around your ICP, CRM, and sales process. You own
              the application code.
            </p>
            <div className='button-row'>
              <LinkButton
                href='/contact-us'
                variant='action'
                size='lg'
                data-cta-id='vector_hero_call'
              >
                Schedule strategy call
              </LinkButton>
              <LinkButton href='#scoring' variant='neutral' size='lg'>
                See how scoring works
              </LinkButton>
            </div>
            <dl className='hero-facts'>
              {HERO_FACTS.map(([term, value]) => (
                <div key={term}>
                  <dt>{term}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div
            ref={heroConsoleRef}
            className='hero-console'
            data-phase={heroPhase}
            role='group'
            aria-label='Illustrative Vector qualification result'
          >
            <div className='console-glow' aria-hidden='true' />
            <div className='console-card'>
              <div className='console-top'>
                <span>Lead intelligence / V-024</span>
                <div className='console-actions'>
                  <span className='live-dot' role='status'>
                    {HERO_STATUS[heroPhase]}
                  </span>
                  <button
                    className='console-replay'
                    type='button'
                    onClick={playHeroSequence}
                    aria-label='Replay the Vector scoring sequence'
                  >
                    <svg viewBox='0 0 16 16' aria-hidden='true'>
                      <path d='M13.2 6.1A5.5 5.5 0 1 0 13 10.4M13.2 2.8v3.3H9.9' />
                    </svg>
                    <span>Replay</span>
                  </button>
                </div>
              </div>
              <div className='console-lead'>
                <div>
                  <div className='lead-id'>High-signal prospect</div>
                  <h2>Sarah Chen</h2>
                  <p>VP of Operations · TechScale Inc.</p>
                </div>
                <div className='score-ring'>
                  <strong>
                    94 <span>/120</span>
                  </strong>
                </div>
              </div>
              <div
                className='route-decision'
                aria-hidden={heroPhase !== 'routed' ? true : undefined}
              >
                <div>
                  <span>Recommended route</span>
                  <strong>Senior Account Executive</strong>
                </div>
                <div className='route-arrow' aria-hidden='true'>
                  ↗
                </div>
              </div>
              <div
                className='mini-scores'
                role='list'
                aria-label='Top qualification scores'
              >
                {MINI_SCORES.map(([name, value, w, c]) => (
                  <div className='mini-score' role='listitem' key={name}>
                    <span>{name}</span>
                    <strong>{value}</strong>
                    <i style={{ '--w': w, '--c': c } as React.CSSProperties} />
                  </div>
                ))}
              </div>
              <div className='console-foot'>
                Illustrative profile · actual weights are configured to your ICP
                and routing rules
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='qualifier-rail' aria-label='Vector fit summary'>
        <div className='qualifier-grid'>
          <div className='qualifier-cell qualifier-intro'>
            <span className='v-rail-label'>Built for teams like yours</span>
            <strong>Steady inbound. Real sales motion.</strong>
          </div>
          {QUALIFIER_CELLS.map(([label, value, note]) => (
            <div className='qualifier-cell' key={label}>
              <span className='v-rail-label'>{label}</span>
              <strong>{value}</strong>
              <span>{note}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        className='section fit-section'
        id='fit'
        aria-labelledby='fit-title'
      >
        <div className='wrap'>
          <div className='fit-head' data-reveal>
            <div>
              <p className='section-kicker'>The qualification gap</p>
              <h2 className='section-title' id='fit-title'>
                Your CRM has the lead. It doesn’t have the answer.
              </h2>
            </div>
            <p className='section-copy'>
              Static lead scores tell you who someone resembles. Vector reads
              what they need, how urgently they need it, and whether the next
              move belongs with sales, nurture, or a human review.
            </p>
          </div>

          <div className='fit-cards' data-reveal>
            {FIT_CARDS.map(([n, title, copy, accent]) => (
              <article
                className='fit-card'
                key={n}
                style={{ '--accent': accent } as React.CSSProperties}
              >
                <div className='fit-icon'>{n}</div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>

          <div className='not-fit' data-reveal>
            <strong>Probably not a fit</strong>
            <p>
              Fewer than 50 leads a month, no CRM, no salesperson to receive
              qualified leads, or routing logic that cannot yet be articulated.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

/** Scoring proof, calculator, safety, ownership, process, pricing, FAQ. */
export function VectorPageMiddle() {
  const { ref, className } = useReveal()
  const [profileKey, setProfileKey] = useState<ProfileKey>('qualified')
  const [leads, setLeads] = useState(100)
  const [minutes, setMinutes] = useState(30)
  const [rate, setRate] = useState(60)

  const profile = PROFILES[profileKey]

  // Same arithmetic as the source's updateCalculator().
  const totalHours = (leads * minutes) / 60
  const lowConversionHours = totalHours * 0.5
  const monthlyWaste = lowConversionHours * rate
  const recoveredHours = lowConversionHours * 0.5
  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  const progress = (value: number, min: number, max: number) =>
    `${((value - min) / (max - min)) * 100}%`

  const onProfileKeyDown = (e: React.KeyboardEvent, key: ProfileKey) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    setProfileKey(key === 'qualified' ? 'filtered' : 'qualified')
  }

  return (
    <div ref={ref} className={className}>
      <section
        className='section product-proof'
        id='scoring'
        aria-labelledby='scoring-title'
      >
        <div className='wrap'>
          <div className='proof-head' data-reveal>
            <div>
              <p className='section-kicker'>What your team sees</p>
              <h2 className='section-title' id='scoring-title'>
                The score. The reason. The route.
              </h2>
            </div>
            <p className='section-copy'>
              Every dimension contributes to a visible decision. Your sellers
              get context before the call, and your operators can inspect or
              override the logic at any time.
            </p>
          </div>

          <div className='proof-app' data-reveal>
            <div className='profile-panel'>
              <div
                className='profile-tabs'
                role='tablist'
                aria-label='Sample lead states'
              >
                {(Object.keys(PROFILES) as ProfileKey[]).map(key => (
                  <button
                    key={key}
                    className='profile-tab'
                    type='button'
                    role='tab'
                    aria-selected={profileKey === key}
                    aria-controls='profile-view'
                    onClick={() => setProfileKey(key)}
                    onKeyDown={e => onProfileKeyDown(e, key)}
                  >
                    {PROFILES[key].tab}
                  </button>
                ))}
              </div>
              <div
                className='profile-badge'
                style={{ color: profile.color, background: profile.background }}
              >
                {profile.badge}
              </div>
              <h3>{profile.name}</h3>
              <p className='profile-role'>{profile.role}</p>
              <div className='profile-total'>
                <div>
                  <span className='data-label'>Total score</span>
                  <div className='profile-score'>
                    <span>{profile.score}</span> <span>/ 120</span>
                  </div>
                </div>
                <div>
                  <span className='data-label'>Routing</span>
                  <strong className='profile-route'>{profile.route}</strong>
                </div>
              </div>
              <div className='profile-outcome'>
                <span className='data-label'>Illustrative outcome</span>
                <span>{profile.outcome}</span>
              </div>
            </div>

            <div
              className='scores-panel'
              id='profile-view'
              role='tabpanel'
              aria-live='polite'
            >
              <div className='scores-title'>
                <h3>12-dimension profile</h3>
                <span>Every score affects routing</span>
              </div>
              <div className='score-grid'>
                {SCORE_ROWS.map(([name, color], i) => {
                  const value = profile.values[i]
                  return (
                    <div className='score-row' key={name}>
                      <strong>{name}</strong>
                      <div className='bar'>
                        <i
                          style={
                            {
                              '--w': `${value * 10}%`,
                              '--c': color,
                            } as React.CSSProperties
                          }
                        />
                      </div>
                      <span>{value}/10</span>
                    </div>
                  )
                })}
              </div>
              <div className='score-rationale'>{profile.rationale}</div>
            </div>
          </div>
          <p className='proof-note'>
            Illustrative product view. Your system is configured to your ICP,
            evidence, scoring weights, CRM fields, and routing rules.
          </p>
        </div>
      </section>

      <section
        className='section calculator-section'
        aria-labelledby='calculator-title'
      >
        <div className='wrap calculator-layout'>
          <div className='calculator-intro' data-reveal>
            <p className='section-kicker'>Estimate the problem size</p>
            <h2 className='section-title' id='calculator-title'>
              How much selling time is trapped in first-pass review?
            </h2>
            <p className='section-copy'>
              Use your current volume, review time, and seller cost. This
              capacity estimate uses a 50% non-conversion midpoint and assumes
              half of that low-conversion review time can be recovered.
            </p>
          </div>

          <div className='calc-card' data-reveal>
            <div className='control-group'>
              <div className='control-head'>
                <label htmlFor='leads'>Monthly inbound leads</label>
                <output className='control-value' htmlFor='leads'>
                  {leads}
                </output>
              </div>
              <input
                id='leads'
                type='range'
                min={50}
                max={500}
                step={10}
                value={leads}
                onChange={e => setLeads(Number(e.target.value))}
                style={
                  {
                    '--progress': progress(leads, 50, 500),
                  } as React.CSSProperties
                }
              />
              <div className='range-bounds'>
                <span>50</span>
                <span>500</span>
              </div>
            </div>

            <div className='control-group'>
              <div className='control-head'>
                <label htmlFor='minutes'>Time per lead</label>
                <output className='control-value' htmlFor='minutes'>
                  {minutes} min
                </output>
              </div>
              <input
                id='minutes'
                type='range'
                min={15}
                max={60}
                step={5}
                value={minutes}
                onChange={e => setMinutes(Number(e.target.value))}
                style={
                  {
                    '--progress': progress(minutes, 15, 60),
                  } as React.CSSProperties
                }
              />
              <div className='range-bounds'>
                <span>15 min</span>
                <span>60 min</span>
              </div>
            </div>

            <div className='control-group'>
              <div className='control-head'>
                <label htmlFor='rate'>Sales rep hourly rate</label>
                <output className='control-value' htmlFor='rate'>
                  ${rate}/hr
                </output>
              </div>
              <input
                id='rate'
                type='range'
                min={30}
                max={150}
                step={5}
                value={rate}
                onChange={e => setRate(Number(e.target.value))}
                style={
                  {
                    '--progress': progress(rate, 30, 150),
                  } as React.CSSProperties
                }
              />
              <div className='range-bounds'>
                <span>$30</span>
                <span>$150</span>
              </div>
            </div>

            <div className='calc-results' aria-live='polite'>
              <div className='calc-result v-featured'>
                <span className='data-label'>
                  Estimated seller time recoverable
                </span>
                <div className='calc-number'>
                  {Math.round(recoveredHours)} <small>hrs/mo</small>
                </div>
              </div>
              <div className='calc-result'>
                <span className='data-label'>Low-conversion review time</span>
                <div className='calc-number'>
                  {Math.round(lowConversionHours)} <small>hrs/mo</small>
                </div>
              </div>
              <div className='calc-result'>
                <span className='data-label'>Low-conversion review cost</span>
                <div className='calc-number'>
                  {money.format(monthlyWaste)} <small>/mo</small>
                </div>
              </div>
            </div>
            <p className='calc-disclaimer'>
              Estimate only—not a promise. Vector measures your real baseline in
              week one, then compares leading indicators every week.
            </p>
          </div>
        </div>
      </section>

      <section
        className='section safety-section'
        aria-labelledby='safety-title'
      >
        <div className='wrap'>
          <div className='safety-head' data-reveal>
            <p className='section-kicker'>Designed to be wrong safely</p>
            <h2 className='section-title' id='safety-title'>
              Automation makes the call. <span>Humans keep authority.</span>
            </h2>
          </div>
          <div className='safety-grid' data-reveal>
            {SAFETY_CARDS.map(([n, title, copy]) => (
              <article className='safety-card' key={n}>
                <div className='safety-number'>{n}</div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <div className='measure-line' data-reveal>
            <p>
              We are not asking you to trust the model. We are asking you to
              measure whether it beats the process you have today.
            </p>
            <span>Baseline → compare → decide</span>
          </div>
        </div>
      </section>

      <section
        className='section ownership-section'
        id='ownership'
        aria-labelledby='ownership-title'
      >
        <div className='wrap'>
          <div className='ownership-head' data-reveal>
            <div>
              <p className='section-kicker'>The ownership boundary</p>
              <h2 className='section-title' id='ownership-title'>
                Your application. Our intelligence platform.
              </h2>
            </div>
            <p className='section-copy'>
              Vector is a build you own, not another login you rent. The
              boundary is explicit before work begins, so your team knows what
              can be maintained, extended, or migrated.
            </p>
          </div>

          <div className='ownership-map' data-reveal>
            <div className='owner-column client'>
              <h3>You own</h3>
              <div className='owner-list'>
                {OWNS.map((item, i) => (
                  <div className='owner-item' key={item}>
                    <span>{`0${i + 1}`}</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className='ownership-core'>
              <div>
                <strong>Vector</strong>
                <span>One operating system</span>
              </div>
            </div>
            <div className='owner-column pixel'>
              <h3>We maintain</h3>
              <div className='owner-list'>
                {MAINTAINS.map((item, i) => (
                  <div className='owner-item' key={item}>
                    <span>{`0${i + 1}`}</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className='section process-section'
        aria-labelledby='process-title'
      >
        <div className='wrap process-layout'>
          <div className='process-intro' data-reveal>
            <p className='section-kicker'>The 60-day build</p>
            <h2 className='section-title' id='process-title'>
              Baseline first. Then build the loop.
            </h2>
            <p className='section-copy'>
              Founder-led delivery, fixed scope, standard infrastructure. Your
              team sees the system take shape against real leads and real
              decisions.
            </p>
          </div>
          <div data-reveal>
            <div className='process-list'>
              {PROCESS.map(([n, title, copy, when]) => (
                <article className='process-step' key={n}>
                  <span className='step-number'>{n}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                  <span className='step-time'>{when}</span>
                </article>
              ))}
            </div>
            <div className='target-card'>
              <div>
                <span>90-day operating target</span>
                <strong>Recover 20–40% of SDR time</strong>
              </div>
              <small>
                Not a guarantee. Measured against your actual week-one baseline.
              </small>
            </div>
          </div>
        </div>
      </section>

      <section
        className='section pricing-section'
        id='pricing'
        aria-labelledby='pricing-title'
      >
        <div className='wrap'>
          <div className='pricing-head' data-reveal>
            <p className='section-kicker'>Build + platform + performance</p>
            <h2 className='section-title' id='pricing-title'>
              A clear price for a system that becomes yours.
            </h2>
          </div>
          <div className='price-grid' data-reveal>
            <article className='v-price-card'>
              <h3 className='price-name'>Build</h3>
              <div className='price-value'>$35,000</div>
              <p>
                One-time, fixed-scope 60-day delivery. You own the application
                code.
              </p>
            </article>
            <article className='v-price-card'>
              <h3 className='price-name'>Platform</h3>
              <div className='price-value'>
                $1,500 <small>/ month</small>
              </div>
              <p>
                Intelligence platform, ongoing training, LLM costs, security
                updates, and priority support.
              </p>
            </article>
            <article className='v-price-card'>
              <h3 className='price-name'>Performance</h3>
              <div className='price-value'>5%</div>
              <p>Of deal value from Vector-qualified leads that close.</p>
            </article>
          </div>
          <div className='price-foot' data-reveal>
            <p>
              12-month platform minimum · 15% discount with annual prepay ·
              final scope confirmed before work begins
            </p>
            {/* Outline, not the amber fill: this band is yellow and the
                filled variant disappears into it. The variant's pink border
                and label are overridden to black for the same reason — same
                pattern FinalCTA uses to re-colour this component for its own
                band. */}
            <LinkButton
              href='/contact-us'
              variant='outline'
              size='lg'
              className='!border-black !text-black hover:!bg-black hover:!text-white'
              data-cta-id='vector_pricing_fit'
            >
              Check if Vector fits
            </LinkButton>
          </div>
        </div>
      </section>

      <section
        className='section faq-section'
        id='faq'
        aria-labelledby='faq-title'
      >
        <div className='wrap faq-layout'>
          <div className='faq-intro' data-reveal>
            <p className='section-kicker'>Questions, answered</p>
            <h2 className='section-title' id='faq-title'>
              Before the call.
            </h2>
            <p className='section-copy'>
              The important commercial, ownership, and operating details—without
              waiting for a deck.
            </p>
          </div>
          <div className='faq-list' data-reveal>
            {FAQS.map(([q, a], i) => (
              <details key={q} open={i === 0}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

/** The closing card. The source's own footer is dropped with the site chrome. */
export function VectorPageFinal() {
  const { ref, className } = useReveal()

  return (
    <div ref={ref} className={className}>
      <section className='final-section'>
        <div className='wrap final-card' data-reveal>
          <h2>Bring the routing logic. Leave with a clear yes or no.</h2>
          <div className='final-copy'>
            <p>
              Thirty minutes. We will ask about lead volume, your CRM, and
              routing complexity. If Vector is not right for your situation, we
              will tell you upfront.
            </p>
            <div className='button-row'>
              <LinkButton
                href='/contact-us'
                variant='action'
                size='lg'
                data-cta-id='vector_final_call'
              >
                Schedule strategy call
              </LinkButton>
              <LinkButton
                href='mailto:founders@pixelmojo.io'
                variant='neutral'
                size='lg'
              >
                Email the founders
              </LinkButton>
            </div>
            <div className='final-meta'>
              No deck · no nurture sequence · one 30-minute fit conversation
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
